import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DEFAULT_DEBOUNCE_CHARACTERS, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';
import { isNotEmpty, setTableViewMode } from 'src/app/extensions/utilities';
import { debounce } from 'src/app/extensions/utilities';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subscription } from 'rxjs';
import { UserEvents } from 'src/app/extensions/services/api/events';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { DateInputRounded } from '@progress/kendo-angular-dateinputs';
import { DeleteItemModalComponent } from 'src/app/extensions/components/delete-item-modal/delete-item-modal.component';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false,
})
export class UserListComponent implements OnInit, OnDestroy {
  public roundedLarge: DateInputRounded = 'large';
  hasData: boolean = false;
  isCompanySelected: boolean = false;

  // comboDataSource: any[] = [];
  // comboValue: any = {};

  autocompleteDatasource: any[] = [];

  autocompleteValue: any = { id: undefined, value: '' };
  hasPartners: boolean = true;
  public subscriptions: Subscription[] = [];

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  windowSizeLgUp: boolean;
  users: app.models.Users[] = [];

  usersProps: app.models.DynamicPropsDictionary = {
    Name: { rendered: true, readonly: false } as app.models.DynamicProps,
    Email: { rendered: true, readonly: false } as app.models.DynamicProps,
    Phone: { rendered: true, readonly: false } as app.models.DynamicProps,
    IsActive: { rendered: true, readonly: false } as app.models.DynamicProps,
    UserRightReadOnly: { rendered: true, readonly: false } as app.models.DynamicProps,
  };

  selectedPartnerTenantId: string;

  partnerSearchValue: string;
  navigation: app.models.UserNavigationState;

  constructor(
    private _globalService: GlobalService,
    private _translateService: TranslateService,
    private _userAPIService: UserAPIService,
    private _ngbModal: NgbModal,
    private _notificationService: NotificationService,
    private _router: Router,
    private _partnerAPIService: PartnerAPIService,
    private _windowEventService: WindowEventsService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
    const currentNav = this._router.getCurrentNavigation();
    this.navigation = currentNav.extras.state as app.models.UserNavigationState;
  }
  ngOnInit() {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    if (this.navigation) {
      let partnerId = this.navigation['partnerId'];
      let partnerName = this.navigation['partnerName'];
      this.autocompleteDatasource.push({ partnerId: partnerId, name: partnerName });
      this.companySelectedHandler(partnerName);
    } else if (history.state && history.state.partnerId && history.state.partnerName) {
      this.autocompleteDatasource.push({ partnerId: history.state.partnerId, name: history.state.partnerName });
      this.companySelectedHandler(history.state.partnerName);
    }

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  async getUsers(externalEntityId: string) {
    this.users = await this._userAPIService.getUsers(externalEntityId).toPromise();
    this.hasData = this.users.length != 0;
  }

  async companySelectedHandler(selectedPartnerTenant: any) {
    this.isCompanySelected = false;
    this.users = [];
    this.selectedPartnerTenantId = undefined;
    if (selectedPartnerTenant) {
      var partnerTenant = this.autocompleteDatasource.find(x => x.name == selectedPartnerTenant);
      if (partnerTenant) {
        this.selectedPartnerTenantId = partnerTenant.partnerId;
        this.autocompleteValue = { id: partnerTenant.partnerId, value: partnerTenant.name };
        await this.getUsers(this.selectedPartnerTenantId);
        this.isCompanySelected = isNotEmpty(selectedPartnerTenant);
      }
    }
  }

  filterDataSource = debounce((value: string) => {
    this.partnerSearchValue = value;
    if (this.partnerSearchValue.length < 3) {
      this.isCompanySelected = false;
    }
    value.length >= DEFAULT_DEBOUNCE_CHARACTERS ? this.loadAutocompleteDateSource(value) : this.clearDataSource();
  });

  async loadAutocompleteDateSource(input: string) {
    this.autocompleteDatasource = await this._partnerAPIService.getPartners(input).toPromise();
    this.hasPartners = this.autocompleteDatasource?.length > 0;
  }

  clearDataSource() {
    this.autocompleteDatasource = [];
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  viewDetailsHandler = (userId: string) => {
    this._router.navigate(['users', this.selectedPartnerTenantId, userId]);
  };

  public deleteUserHandler = (userId: string = null) => {
    if (userId) {
      const deleteUserModal = this._ngbModal.open(DeleteItemModalComponent, { backdrop: 'static' });
      var user = this.users.find(x => x.userId == userId);

      deleteUserModal.componentInstance.title = 'users.deleteUserTitle';
      deleteUserModal.componentInstance.item = user.name;
      deleteUserModal.componentInstance.warninglabel = this._translateService.instant('users.deleteUserWarning');
      deleteUserModal.componentInstance.values = [
        this._translateService.instant('users.access.dashboards'),
        this._translateService.instant('users.access.contractDetail'),
        this._translateService.instant('users.access.assetsInfo'),
        this._translateService.instant('users.access.invoices'),
        this._translateService.instant('users.access.paymentHistory'),
        this._translateService.instant('users.access.requests'),
      ];

      deleteUserModal.result.then(result => {
        if (result) {
          this._userAPIService.deleteUser(userId, this.selectedPartnerTenantId).subscribe(deleteUserResponse => {
            if (deleteUserResponse.isSuccess) {
              this.users = this.users.filter(x => x.userId != userId);
              this._notificationService.showSuccess(
                this._translateService.instant('users.add.notifications.userDeleted'),
                this._translateService.instant('common.success')
              );
            } else {
              const userDeleteFailedEvent = deleteUserResponse.response.events.find(
                x => x.eventType === UserEvents.UserDeleteFailed
              ).eventBody;
              if (userDeleteFailedEvent.errorMessage === 'ERROR_USER_CREATED_DATA') {
                this._notificationService.showWarning(
                  this._translateService.instant('users.add.notifications.userCreatedData'),
                  this._translateService.instant('common.success')
                );
              } else {
                this._notificationService.showWarning(
                  userDeleteFailedEvent.errorMessage,
                  this._translateService.instant('common.warning')
                );
              }
            }
          });
        }
      });
    }
  };

  public userStatusHandler = async (userId: string, futureUserState: any) => {
    if (userId) {
      var user = this.users.find(x => (x.userId = userId));
      if (user) {
        this._userAPIService
          .changeUserState(userId, futureUserState, this.selectedPartnerTenantId)
          .subscribe(statusChangeResponse => {
            if (statusChangeResponse.isSuccess) {
              this._notificationService.showSuccess(
                this._translateService.instant('users.add.notifications.userStatusChanged'),
                this._translateService.instant('common.success')
              );
            }
          });
      }
    }
  };

  navigateAddEditFormHandler(subTenantId: string, userId: string = null) {
    if (userId) {
      this._router.navigate(['users', subTenantId, userId]);
    } else {
      this._router.navigate(['users/new', subTenantId]);
    }
  }

  public redirectUserDetails = (userId: string) => {
    this._router.navigate(['users', userId]);
  };

  public redirectUserRights = (userId: string) => {
    const navigationExtras = {
      state: { partnerName: this.autocompleteValue.value, partnerId: this.autocompleteValue.id },
    };
    this._router.navigate(['users/rights-configurator', userId], navigationExtras);
  };
}
